import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getVisuelPath, resolveMediaUrl } from '../utils/media';
import { resolveMinisiteFont, resolveMinisiteColor, minisiteFontsHref } from '../utils/minisiteTheme';
import { useOccasion, resolveBlocs } from '../utils/minisiteOccasions';
import { useT, useLang, useFormat, useRecipientLang, LANGUAGES, DEFAULT_LANG, getLanguage } from '../i18n';
import { I18nContext } from '../i18n/context';
import MinisiteRsvpForm from './MinisiteRsvpForm';
import './Minisite.css';

// Rendu du mini-site de mariage à partir de cartes_v2.params. Un seul composant pour la page
// publique (MinisitePage.js), l'aperçu de l'éditeur (MinisiteEditor.js) et la démo live de la
// fiche produit (ProductDetail.js) : la démo montre exactement ce que le client obtient.
//
// Le fond est le visuel BRUT (getVisuelPath), pas la démo carte-à-gratter (getDemoPath) : cette
// dernière est un mockup de téléphone avec cœur à gratter incrusté dans l'image.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Dates et heures dans la langue de la page (Intl). En français : « Samedi 12 juin 2027 » et « 15h30 »,
// exactement comme avant.
const DATE_OPTIONS = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

// Forme naturelle de la langue (sans majuscule forcée) : « samedi 12 juin 2027 », « Samstag, 12. Juni 2027 ».
const formatDateLongue = (date, fmt) => (
  date && ISO_DATE.test(date) ? fmt.dateTime(new Date(`${date}T12:00:00`), DATE_OPTIONS) : ''
);

const formatDate = (date, fmt) => {
  const formatted = formatDateLongue(date, fmt);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const formatHeure = (heure, lang, fmt) => {
  if (!/^\d{2}:\d{2}$/.test(heure || '')) return '';
  if (lang === DEFAULT_LANG) return heure.replace(':', 'h').replace(/h00$/, 'h');
  const [h, m] = heure.split(':').map(Number);
  return fmt.dateTime(new Date(2000, 0, 1, h, m), { hour: 'numeric', minute: '2-digit' });
};

const getRemaining = (date, heure) => {
  if (!date || !ISO_DATE.test(date)) return null;
  const time = /^\d{2}:\d{2}$/.test(heure || '') ? heure : '00:00';
  const ms = new Date(`${date}T${time}:00`).getTime() - Date.now();
  if (ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

const useCountdown = (date, heure) => {
  const [remaining, setRemaining] = useState(() => getRemaining(date, heure));
  useEffect(() => {
    setRemaining(getRemaining(date, heure));
    const timer = setInterval(() => setRemaining(getRemaining(date, heure)), 1000);
    return () => clearInterval(timer);
  }, [date, heure]);
  return remaining;
};

// "Camille & Julien" / "Camille et Julien" → chaque prénom arrive l'un après l'autre. `mot` : « et » de la
// langue de la page (and, und, y, e) : « Anna und Ben » se coupe aussi ; « et » reste reconnu partout.
const splitPrenoms = (prenoms, mot = 'et') => {
  const conjonction = String(mot).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = prenoms.split(new RegExp(`\\s+(?:&|et|${conjonction})\\s+`, 'i'));
  return parts.length === 2 ? [parts[0], '&', parts[1]] : [prenoms];
};

// `maps` : coordonnées GPS ("43.52, 5.44") ou lien Google Maps collé par le client. Seuls les
// liens https de Google sont repris tels quels ; sinon recherche sur le lieu + adresse.
const resolveMaps = ({ maps, lieu, adresse }) => {
  const raw = (maps || '').trim();
  const coords = raw.match(/^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  const query = coords ? `${coords[1]},${coords[2]}` : [lieu, adresse].filter(Boolean).join(', ');
  if (!query && !raw) return null;
  const isGoogleLink = /^https:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps|(www\.)?google\.[a-z.]+\/maps)/i.test(raw);
  return {
    link: isGoogleLink ? raw : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    embed: query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : null,
  };
};

const Names = ({ prenoms, animation, mot }) => {
  const parts = splitPrenoms(prenoms, mot);
  if (animation === 'lettres') {
    let charIndex = 0;
    return parts.map((part, partIndex) => (
      <span key={partIndex} className={`ms-name-line${part === '&' ? ' ms-name-amp' : ''}`}>
        {[...part].map((char, i) => {
          charIndex += 1;
          return (
            <span key={i} className="ms-letter" style={{ animationDelay: `${0.5 + charIndex * 0.07}s` }}>
              {char === ' ' ? ' ' : char}
            </span>
          );
        })}
      </span>
    ));
  }
  return parts.map((part, index) => (
    <span
      key={index}
      className={`ms-name-part${part === '&' ? ' ms-name-amp' : ''}`}
      style={{ animationDelay: `${0.6 + index * 0.45}s` }}
    >
      {part}
    </span>
  ));
};

// `statique` : sans animation d'arrivée (captures d'écran des fiches, minisite-factory/).
const MinisiteViewContent = ({ params = {}, slug, embedded = false, statique = false }) => {
  const t = useT('minisite-view');
  const { lang } = useLang();
  const fmt = useFormat();
  const occasion = useOccasion(params);
  const {
    theme_visuel: visuel, police, police_texte: policeTexte, couleur, ambiance, animation = 'douce',
    disposition = 'centre', taille_prenoms: taillePrenoms = 100, voile = 70,
    intro, prenoms, sous_titre: sousTitre, date, heure, lieu, adresse, texte, signature,
    programme, programme_titre: programmeTitre, compte_a_rebours: compteARebours, carte_lieu: carteLieu,
    details, details_titre: detailsTitre,
    cagnotte_url: cagnotteUrl, cagnotte_titre: cagnotteTitre, cagnotte_texte: cagnotteTexte,
    rsvp_actif: rsvpActif, rsvp_titre: rsvpTitre, rsvp_message: rsvpMessage, rsvp_date_limite: rsvpDateLimite,
    photo, photo_forme: photoForme = 'rond', photo_emplacement: photoEmplacement = 'accueil',
    fond_perso: fondPerso, fond_perso_actif: fondPersoActif, fond_cadrage: fondCadrage = 'centre',
  } = params;
  const remaining = useCountdown(date, heure);
  // Fond : celui envoyé par le client s'il existe (et n'a pas été désactivé), sinon le visuel acheté.
  const useFondPerso = fondPerso && fondPersoActif !== false;
  const bgImage = useFondPerso ? resolveMediaUrl(fondPerso) : (visuel ? resolveMediaUrl(getVisuelPath(visuel)) : null);
  const bgPosition = { haut: 'center top', bas: 'center bottom' }[useFondPerso ? fondCadrage : ''] || 'center';
  const displayedNames = prenoms || occasion.nomsPlaceholder;
  const mot = t('minisite-view.namesConjunction');
  const nameUnits = animation === 'lettres' ? [...displayedNames].length * 0.07 : splitPrenoms(displayedNames, mot).length * 0.45;
  const afterNames = 0.6 + nameUnits;
  const maps = resolveMaps(params);
  const etapes = Array.isArray(programme) ? programme.filter((e) => e && e.titre) : [];
  const lignes = Array.isArray(details) ? details.filter((d) => d && d.valeur) : [];
  const fontsHref = minisiteFontsHref([police, policeTexte]);
  const heureLue = formatHeure(heure, lang, fmt);
  const dateLine = [formatDate(date, fmt), heureLue && t('minisite-view.at', { time: heureLue })].filter(Boolean).join(' ');
  const photoUrl = photo ? resolveMediaUrl(photo) : null;
  const photoEl = photoUrl && <img className={`ms-photo ms-photo-${photoForme}`} src={photoUrl} alt="" />;
  const cagnotteOk = /^https:\/\//.test(cagnotteUrl || '');

  const title = (text) => <div className="ms-panel-title" role="heading" aria-level="2">{text}</div>;

  // Une section ne s'affiche que si elle a du contenu ou si son interrupteur est activé.
  const blocs = {
    texte: texte && (
      <section className="ms-panel" key="texte">
        <p className="ms-texte">{texte}</p>
        {signature && <p className="ms-signature">{signature}</p>}
      </section>
    ),
    photo: photoEl && photoEmplacement === 'page' && (
      <section className="ms-panel ms-panel-photo" key="photo">{photoEl}</section>
    ),
    details: lignes.length > 0 && (
      <section className="ms-panel" key="details">
        {detailsTitre && title(detailsTitre)}
        <dl className="ms-details">
          {lignes.map((ligne, index) => (
            <div key={index}>
              {ligne.label && <dt>{ligne.label}</dt>}
              <dd>{ligne.valeur}</dd>
            </div>
          ))}
        </dl>
      </section>
    ),
    programme: etapes.length > 0 && (
      <section className="ms-panel" key="programme">
        {title(programmeTitre || t('minisite-view.program'))}
        <ul className="ms-programme">
          {etapes.map((etape, index) => (
            <li key={index}>
              <span className="ms-programme-heure">{etape.heure}</span>
              <span className="ms-programme-titre">{etape.titre}</span>
            </li>
          ))}
        </ul>
      </section>
    ),
    compte_a_rebours: remaining && compteARebours !== false && occasion.compteARebours && (
      <section className="ms-panel" key="compte_a_rebours">
        {title(occasion.compteARebours)}
        <div className="ms-countdown">
          {Object.entries(remaining).map(([unit, value]) => (
            <div key={unit}>
              <span className="ms-countdown-value">{String(value).padStart(2, '0')}</span>
              <span className="ms-countdown-label">{t(`minisite-view.countdown.${unit}`, { count: value })}</span>
            </div>
          ))}
        </div>
      </section>
    ),
    lieu: (adresse || (lieu && maps)) && (
      <section className="ms-panel" key="lieu">
        {title(t('minisite-view.place'))}
        {lieu && <p className="ms-texte"><strong>{lieu}</strong></p>}
        {adresse && <p className="ms-texte">{adresse}</p>}
        {maps?.embed && carteLieu !== false && (
          <iframe className="ms-map" title={t('minisite-view.mapTitle')} src={maps.embed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        )}
        {maps && <a className="ms-link" href={maps.link} target="_blank" rel="noreferrer">{t('minisite-view.openMaps')}</a>}
      </section>
    ),
    rsvp: rsvpActif && (
      <section className="ms-panel" key="rsvp">
        {title(rsvpTitre || t('minisite-view.rsvpTitle'))}
        {rsvpMessage && <p className="ms-texte">{rsvpMessage}</p>}
        {formatDateLongue(rsvpDateLimite, fmt) && (
          <p className="ms-muted">{t('minisite-view.rsvpDeadline', { date: formatDateLongue(rsvpDateLimite, fmt) })}</p>
        )}
        <MinisiteRsvpForm slug={slug} params={params} preview={embedded} />
      </section>
    ),
    cagnotte: cagnotteOk && (
      <section className="ms-panel" key="cagnotte">
        {title(cagnotteTitre || t('minisite-view.giftTitle'))}
        {cagnotteTexte && <p className="ms-texte">{cagnotteTexte}</p>}
        <a className="ms-link" href={cagnotteUrl} target="_blank" rel="noreferrer noopener">{t('minisite-view.giftButton')}</a>
      </section>
    ),
  };

  return (
    <div
      className={`ms-root ${embedded ? 'ms-embedded' : 'ms-fullpage'} ms-ambiance-${ambiance === 'sombre' ? 'sombre' : 'claire'} ms-anim-${animation}${statique ? ' ms-static' : ''}`}
      style={{
        '--ms-font': resolveMinisiteFont(police),
        '--ms-font-texte': resolveMinisiteFont(policeTexte, 'dm-sans'),
        '--ms-accent': resolveMinisiteColor(couleur),
        '--ms-names-scale': Math.max(60, Math.min(160, Number(taillePrenoms) || 100)) / 100,
        '--ms-voile': Math.max(0, Math.min(100, Number(voile))) / 100,
      }}
    >
      {fontsHref && <Helmet><link rel="stylesheet" href={fontsHref} /></Helmet>}
      <div className="ms-bg" style={bgImage ? { backgroundImage: `url("${bgImage}")`, backgroundPosition: bgPosition } : undefined} />
      <div className="ms-scroll">
        <section className={`ms-hero ms-hero-${disposition}`}>
          <div className="ms-hero-inner">
            {photoEl && photoEmplacement !== 'page' && <div className="ms-anim" style={{ animationDelay: '0.1s' }}>{photoEl}</div>}
            <p className="ms-intro ms-anim" style={{ animationDelay: '0.2s' }}>{intro || occasion.intro}</p>
            {/* div et non h1/h2 : App.css impose police/taille/couleur des titres en !important. */}
            <div className="ms-names" role="heading" aria-level="1">
              <Names prenoms={displayedNames} animation={animation} mot={mot} />
            </div>
            {sousTitre && (
              <p className="ms-sous-titre ms-anim" style={{ animationDelay: `${afterNames + 0.2}s` }}>{sousTitre}</p>
            )}
            <div className="ms-divider ms-anim" style={{ animationDelay: `${afterNames + 0.4}s` }} />
            <p className="ms-date ms-anim" style={{ animationDelay: `${afterNames + 0.7}s` }}>
              {dateLine || occasion.dateLabel}
            </p>
            {(lieu || !prenoms) && (
              <p className="ms-lieu ms-anim" style={{ animationDelay: `${afterNames + 1}s` }}>{lieu || t('minisite-view.heroPlace')}</p>
            )}
          </div>
          <div className="ms-scroll-hint ms-anim" style={{ animationDelay: `${afterNames + 1.7}s` }}>
            <span>⌄</span>
          </div>
        </section>

        {resolveBlocs(params).map((key) => blocs[key] || null)}

        <footer className="ms-footer">{t('minisite-view.footer')}</footer>
      </div>
    </div>
  );
};

// ─── Langue de la page ─────────────────────────────────────────────────────────────────────────────
// La langue d'une page de faire-part est le réglage `langue` de l'acheteur (jamais celle du navigateur du
// visiteur) ; sans réglage, le français comme avant.
const knownLang = (code) => (LANGUAGES.some((l) => l.code === code) ? code : null);

// Page publique (/i/<code>, /apercu-mini-site) : la page impose sa langue à tout l'écran. On n'affiche rien
// tant qu'elle n'est pas appliquée (pas de texte dans la mauvaise langue qui clignote).
const PageLang = (props) => {
  const wanted = knownLang(props.langue || props.params?.langue) || DEFAULT_LANG;
  const { lang } = useLang();
  useRecipientLang(wanted);
  if (lang !== wanted) return null;
  return <MinisiteViewContent {...props} />;
};

// Aperçu (embedded : éditeur de l'acheteur, démo de la fiche produit) : la langue de la PAGE (`langue`) ne
// s'applique qu'à cet arbre grâce à un mini-fournisseur ; la langue de l'interface autour reste inchangée.
// Sans `langue`, l'aperçu suit la langue de l'interface. Voir docs/multilingue-lot-e-contrat.md.
const ScopedLang = (props) => {
  const ctx = useContext(I18nContext);
  const forced = knownLang(props.langue);
  const value = useMemo(
    () => (forced && forced !== ctx.lang ? { ...ctx, lang: forced, locale: getLanguage(forced).locale } : ctx),
    [ctx, forced],
  );
  return (
    <I18nContext.Provider value={value}>
      <Suspense fallback={null}><MinisiteViewContent {...props} /></Suspense>
    </I18nContext.Provider>
  );
};

const MinisiteView = (props) => (props.embedded ? <ScopedLang {...props} /> : <PageLang {...props} />);

export default MinisiteView;
